import { useMemo } from "react";
import { Plus } from "lucide-react";
import { Link } from "react-router-dom";
import Card from "@/components/common/Card";
import MultiSelect from "@/components/common/MultiSelect";

export default function Step2Target({
  form,
  segments,
  onFieldChange,
  onNumberChange,
}) {
  const segmentOptions = useMemo(
    () =>
      segments
        .map((segment) => ({
          value: segment.uuid,
          label: segment.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [segments],
  );

  return (
    <Card className="builder-form flex flex-col gap-5">
      <div>
        <h3 className="text-base font-semibold text-(--text-primary)">
          Target audiences
        </h3>
        <p className="mt-1 text-sm text-(--text-secondary)">
          Choose which audiences this campaign should reach. Leave empty to
          target everyone.
        </p>
        <div className="mt-3">
          {segmentOptions.length > 0 ? (
            <MultiSelect
              options={segmentOptions}
              value={form.selectedSegments}
              onChange={(next) => onFieldChange("selectedSegments", next)}
              placeholder="Search audiences..."
            />
          ) : (
            <p className="form-help-text">
              No audiences available yet.{" "}
              <Link
                to="/dashboard/segments/new"
                className="font-medium text-(--brand-primary) hover:underline"
              >
                <Plus size={12} strokeWidth={2.5} className="inline" /> Create
                an audience
              </Link>{" "}
              first to target it.
            </p>
          )}
        </div>
      </div>

      <div className="builder-divider" aria-hidden />

      <div className="builder-subsection">
        <h4>Budget &amp; delivery</h4>
        <p className="subsection-description">
          Optional caps and CPM override for this campaign.
        </p>
        <div className="builder-grid two-columns">
          <div className="form-group-modal">
            <label htmlFor="campaign-cpm">CPM ($)</label>
            <input
              id="campaign-cpm"
              type="number"
              min="0"
              step="0.01"
              value={form.cpm ?? 0}
              onChange={(event) => onNumberChange("cpm", event.target.value)}
              placeholder="e.g. 2.50"
            />
          </div>
          <div className="form-group-modal">
            <label htmlFor="campaign-budget-cap">Budget cap ($)</label>
            <input
              id="campaign-budget-cap"
              type="number"
              min="0"
              step="0.01"
              value={form.budgetCap ?? ""}
              onChange={(event) =>
                onFieldChange("budgetCap", event.target.value)
              }
              placeholder="e.g. 5000"
            />
          </div>
        </div>
        <div className="builder-grid three-columns">
          <div className="form-group-modal">
            <label htmlFor="campaign-daily-freq">Daily frequency cap</label>
            <input
              id="campaign-daily-freq"
              type="number"
              min="1"
              value={form.dailyFrequency ?? ""}
              onChange={(event) =>
                onFieldChange("dailyFrequency", event.target.value)
              }
              placeholder="e.g. 3"
            />
          </div>
          <div className="form-group-modal">
            <label htmlFor="campaign-weekly-freq">Weekly frequency cap</label>
            <input
              id="campaign-weekly-freq"
              type="number"
              min="1"
              value={form.weeklyFrequency ?? ""}
              onChange={(event) =>
                onFieldChange("weeklyFrequency", event.target.value)
              }
              placeholder="e.g. 10"
            />
          </div>
          <div className="form-group-modal">
            <label htmlFor="campaign-impressions-cap">Impressions cap</label>
            <input
              id="campaign-impressions-cap"
              type="number"
              min="1"
              value={form.impressionsCap ?? ""}
              onChange={(event) =>
                onFieldChange("impressionsCap", event.target.value)
              }
              placeholder="e.g. 100000"
            />
          </div>
        </div>
      </div>
    </Card>
  );
}
